import { formatarCpf, formatarPlaca, registrarMultaSchema } from '@locaveri/shared'
import { LoaderCircle, UserCheck } from 'lucide-react'
import { type ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Campo } from '@/components/form/campo'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { diasAte, identificacaoDoVeiculo } from '@/features/frota/api'
import { paraInstante } from '@/features/precos/dialogo-de-bloqueio'
import { errorMessage } from '@/lib/api'
import { deCampoDeReaisParaWire } from '@/lib/dinheiro'
import { formatarDataHora } from '@/lib/datas'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'

import {
  DIAS_DE_ALERTA_DO_PRAZO,
  type Multa,
  useCondutoresDaReserva,
  useFrotaParaSelecao,
  useIndicarCondutor,
  useRegistrarMulta,
} from './api'

/**
 * Os dois diálogos de multa: o REGISTRO (a notificação como chega do órgão) e
 * a INDICAÇÃO do condutor.
 *
 * ---------------------------------------------------------------------------
 * O REGISTRO NÃO TEM CAMPO DE RESERVA
 * ---------------------------------------------------------------------------
 * De propósito, e o formulário diz isso em voz alta: a vinculação é da API,
 * que cruza a placa do veículo com a data da infração sobre as alocações.
 * Digitar a reserva é como a multa do cliente A vai para a fatura do B — o
 * mesmo motivo de `registrarMultaSchema` não ter `reservationId`.
 *
 * ---------------------------------------------------------------------------
 * A INDICAÇÃO SÓ OFERECE CONDUTORES DA RESERVA
 * ---------------------------------------------------------------------------
 * A lista vem do detalhe da reserva vinculada — nunca de um campo livre de
 * CPF: indicar quem não estava no contrato é falsidade na comunicação ao
 * órgão. A API confere de novo do lado dela; aqui a tela simplesmente não
 * deixa escolher errado.
 */

// ---------------------------------------------------------------------------
// Registro
// ---------------------------------------------------------------------------

const CAMPOS = [
  'vehicleId',
  'dataDaInfracao',
  'autoDeInfracao',
  'orgaoAutuador',
  'descricao',
  'valor',
  'pontos',
  'prazoParaIndicacao',
] as const

interface FormularioDeMulta {
  vehicleId: string
  /** Hora de parede do `datetime-local`; vira instante com fuso no envio. */
  dataDaInfracao: string
  autoDeInfracao: string
  orgaoAutuador: string
  descricao: string
  /** Reais digitados (`"195,23"`), convertidos para centavos no envio. */
  valor: string
  pontos: string
  /** `AAAA-MM-DD` — o prazo legal impresso na notificação. */
  prazoParaIndicacao: string
}

const VALORES_INICIAIS: FormularioDeMulta = {
  vehicleId: '',
  dataDaInfracao: '',
  autoDeInfracao: '',
  orgaoAutuador: '',
  descricao: '',
  valor: '',
  pontos: '',
  prazoParaIndicacao: '',
}

export interface DialogoDeMultaProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
}

export function DialogoDeMulta({ aberto, aoFechar }: DialogoDeMultaProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)

  const frota = useFrotaParaSelecao()
  const registrar = useRegistrarMulta()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    formState: { errors },
  } = useForm<FormularioDeMulta>({ defaultValues: VALORES_INICIAIS })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, a segunda
  // multa do dia abriria com os dados da primeira.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(VALORES_INICIAIS)
    }
  }, [aberto, reset])

  const ocupado = registrar.isPending

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    const instante = paraInstante(valores.dataDaInfracao)

    if (instante === null) {
      setError('dataDaInfracao', {
        type: 'validate',
        message: 'informe a data e a hora completas da infração',
      })
      return
    }

    const valorWire = deCampoDeReaisParaWire(valores.valor)

    if (valorWire === null) {
      setError('valor', { type: 'validate', message: 'valor inválido — use o formato 195,23' })
      return
    }

    const entrada = {
      vehicleId: valores.vehicleId,
      dataDaInfracao: instante,
      autoDeInfracao: valores.autoDeInfracao,
      orgaoAutuador: valores.orgaoAutuador,
      descricao: valores.descricao,
      valor: valorWire,
      pontos: valores.pontos,
      prazoParaIndicacao: valores.prazoParaIndicacao,
    }

    const validado = registrarMultaSchema.safeParse(entrada)

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS))
      return
    }

    // O que viaja é `entrada`, e não `validado.data`: o schema transforma o
    // valor em `bigint` e a data em `Date`, e `JSON.stringify` de `bigint`
    // lança.
    registrar.mutate(entrada, {
      onSuccess: (salva) => {
        toast.success(`Multa ${salva.autoDeInfracao} registrada`, {
          description:
            salva.reservationId === null
              ? 'Nenhuma reserva cobria a placa na data da infração — ficou sem vínculo, e a cobrança fica na operação.'
              : 'Vinculada automaticamente à reserva que estava com o carro. Indique o condutor antes do prazo.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS))
      },
    })
  })

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Registrar multa</DialogTitle>
          <DialogDescription>
            Copie a notificação como ela chegou do órgão. A vinculação à reserva é automática — o
            sistema cruza a placa com a data da infração; não há campo de reserva.
          </DialogDescription>
        </DialogHeader>

        <form
          noValidate
          onSubmit={(evento) => {
            void aoEnviar(evento)
          }}
          className="space-y-4"
        >
          {erroGeral === null ? null : (
            <Alert variant="danger">
              <AlertDescription>{erroGeral}</AlertDescription>
            </Alert>
          )}

          {frota.isError ? (
            <Alert variant="danger">
              <AlertTitle>Não foi possível carregar a frota</AlertTitle>
              <AlertDescription className="flex flex-wrap items-center gap-2">
                {errorMessage(frota.error)}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void frota.refetch()
                  }}
                >
                  Tentar de novo
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Veículo autuado"
              obrigatorio
              erro={errors.vehicleId?.message}
              className="sm:col-span-2"
              dica="Inclui carros já baixados — a notificação chega semanas depois da infração."
            >
              {(campo) => (
                <Select
                  {...campo}
                  {...register('vehicleId', { required: 'escolha o veículo autuado' })}
                  disabled={ocupado || frota.isPending}
                >
                  <option value="">
                    {frota.isPending ? 'Carregando a frota…' : 'Escolha o veículo'}
                  </option>
                  {(frota.data?.itens ?? []).map((veiculo) => (
                    <option key={veiculo.id} value={veiculo.id}>
                      {identificacaoDoVeiculo(veiculo)}
                    </option>
                  ))}
                </Select>
              )}
            </Campo>

            <Campo
              rotulo="Data e hora da infração"
              obrigatorio
              erro={errors.dataDaInfracao?.message}
              dica="No fuso do ponto de retirada — é ela que acha a reserva."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('dataDaInfracao', {
                    required: 'informe a data e a hora da infração',
                  })}
                  type="datetime-local"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Auto de infração"
              obrigatorio
              erro={errors.autoDeInfracao?.message}
              dica="O número impresso na notificação."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('autoDeInfracao', { required: 'informe o auto de infração' })}
                  maxLength={40}
                  placeholder="AB12345678"
                  className="uppercase"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo rotulo="Órgão autuador" obrigatorio erro={errors.orgaoAutuador?.message}>
              {(campo) => (
                <Input
                  {...campo}
                  {...register('orgaoAutuador', { required: 'informe o órgão autuador' })}
                  maxLength={80}
                  placeholder="DETRAN-SP"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Valor"
              obrigatorio
              erro={errors.valor?.message}
              dica="Em reais. Ex.: 195,23"
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('valor', {
                    required: 'informe o valor da multa',
                    validate: (valor: string) =>
                      deCampoDeReaisParaWire(valor) !== null ||
                      'valor inválido — use o formato 195,23',
                  })}
                  inputMode="decimal"
                  placeholder="0,00"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Pontos na CNH"
              obrigatorio
              erro={errors.pontos?.message}
              dica="De 0 a 7 — a infração gravíssima vale 7."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('pontos', { required: 'informe os pontos' })}
                  type="number"
                  min={0}
                  max={7}
                  inputMode="numeric"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>

            <Campo
              rotulo="Prazo para indicar o condutor"
              obrigatorio
              erro={errors.prazoParaIndicacao?.message}
              dica="O prazo legal impresso na notificação. Perdê-lo dobra a multa (NIC)."
            >
              {(campo) => (
                <Input
                  {...campo}
                  {...register('prazoParaIndicacao', {
                    required: 'informe o prazo de indicação',
                  })}
                  type="date"
                  className="tabular-money"
                  disabled={ocupado}
                />
              )}
            </Campo>
          </div>

          <Campo
            rotulo="Descrição da infração"
            obrigatorio
            erro={errors.descricao?.message}
            dica="Como está na notificação — é o texto que o cliente vai ler na cobrança."
          >
            {(campo) => (
              <Textarea
                {...campo}
                {...register('descricao', { required: 'descreva a infração' })}
                rows={3}
                maxLength={1000}
                placeholder="Transitar em velocidade superior à máxima permitida em até 20%"
                disabled={ocupado}
              />
            )}
          </Campo>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
              Cancelar
            </Button>
            <Button type="submit" disabled={ocupado}>
              {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
              Registrar multa
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Indicação de condutor
// ---------------------------------------------------------------------------

function tituloDoPrazo(dias: number): string {
  if (dias < 0) {
    return 'O prazo de indicação já venceu'
  }

  if (dias === 0) {
    return 'O prazo de indicação vence hoje'
  }

  return `O prazo de indicação vence em ${String(dias)} dia${dias === 1 ? '' : 's'}`
}

export interface DialogoDeIndicacaoDeCondutorProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  /** A multa em questão. `null` enquanto nenhuma linha foi escolhida. */
  readonly multa: Multa | null
}

export function DialogoDeIndicacaoDeCondutor({
  aberto,
  aoFechar,
  multa,
}: DialogoDeIndicacaoDeCondutorProps): ReactElement {
  const [condutorId, setCondutorId] = useState('')

  const reservationId = multa === null ? null : multa.reservationId
  const consulta = useCondutoresDaReserva(aberto ? reservationId : null)
  const indicar = useIndicarCondutor()

  // Pré-seleciona o condutor já indicado (reindicação corrige um engano).
  useEffect(() => {
    if (aberto) {
      setCondutorId(multa?.driverId ?? '')
    }
  }, [aberto, multa])

  const dias = multa === null ? null : diasAte(multa.prazoParaIndicacao)

  function confirmar(): void {
    if (multa === null || condutorId.length === 0) {
      return
    }

    indicar.mutate(
      { id: multa.id, condutorId },
      {
        onSuccess: (salva) => {
          toast.success('Condutor indicado', {
            description: `${salva.condutorNome ?? 'O condutor'} será comunicado ao ${salva.orgaoAutuador} como condutor no momento da infração.`,
          })
          aoFechar()
        },
        // O 422 da API (condutor de outra reserva, prazo vencido, status que
        // não permite) chega aqui com a mensagem dela — a tela só repassa.
        onError: (causa: unknown) => {
          toast.error('Não foi possível indicar o condutor', {
            description: errorMessage(causa),
          })
        },
      },
    )
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Indicar condutor</DialogTitle>
          <DialogDescription>
            {multa === null
              ? 'Escolha uma multa na lista.'
              : `Auto ${multa.autoDeInfracao} · ${formatarPlaca(multa.veiculoPlaca)} · ${formatarDataHora(multa.dataDaInfracao)}. Só condutores da reserva vinculada podem ser indicados ao órgão.`}
          </DialogDescription>
        </DialogHeader>

        {dias !== null && dias <= DIAS_DE_ALERTA_DO_PRAZO ? (
          <Alert variant="danger">
            <AlertTitle>{tituloDoPrazo(dias)}</AlertTitle>
            <AlertDescription>
              Sem a indicação dentro do prazo, o órgão emite a multa NIC — o valor DOBRA e cai sobre
              a locadora.
            </AlertDescription>
          </Alert>
        ) : null}

        {reservationId === null ? (
          <p className="text-sm text-fg-secondary">
            Esta multa não tem reserva vinculada — não há condutor de contrato para indicar. Nenhuma
            alocação cobria a placa na data da infração.
          </p>
        ) : consulta.isPending ? (
          <div role="status" aria-live="polite" aria-busy="true" className="space-y-2">
            <span className="sr-only">Carregando os condutores da reserva…</span>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </div>
        ) : consulta.isError ? (
          <Alert variant="danger">
            <AlertTitle>Não foi possível carregar os condutores</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-2">
              {errorMessage(consulta.error)}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => {
                  void consulta.refetch()
                }}
              >
                Tentar de novo
              </Button>
            </AlertDescription>
          </Alert>
        ) : consulta.data.condutores.length === 0 ? (
          <p className="text-sm text-fg-secondary">
            A reserva vinculada não tem condutor cadastrado — o que não deveria acontecer. Confira a
            reserva {`#${String(consulta.data.numero)}`} antes de indicar.
          </p>
        ) : (
          <fieldset className="space-y-2" disabled={indicar.isPending}>
            <legend className="sr-only">Condutores da reserva vinculada</legend>
            {consulta.data.condutores.map((condutor) => (
              <label
                key={condutor.id}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border
                  bg-surface p-3 text-sm has-[:checked]:border-border-strong
                  has-[:checked]:bg-elevated"
              >
                <input
                  type="radio"
                  name="condutor-indicado"
                  value={condutor.id}
                  checked={condutorId === condutor.id}
                  onChange={() => {
                    setCondutorId(condutor.id)
                  }}
                  className="mt-1"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-foreground">{condutor.nome}</span>
                    {condutor.principal ? <Badge variant="neutral">Principal</Badge> : null}
                    {multa?.driverId === condutor.id ? (
                      <Badge variant="info">
                        <UserCheck aria-hidden="true" />
                        Já indicado
                      </Badge>
                    ) : null}
                  </span>
                  <span className="block text-xs tabular-money text-fg-secondary">
                    CPF {formatarCpf(condutor.cpf)}
                  </span>
                </span>
              </label>
            ))}
          </fieldset>
        )}

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={indicar.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={confirmar}
            disabled={indicar.isPending || condutorId.length === 0 || reservationId === null}
          >
            {indicar.isPending ? (
              <LoaderCircle className="animate-spin" aria-hidden="true" />
            ) : (
              <UserCheck aria-hidden="true" />
            )}
            Indicar condutor
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
